import {
  FeEmptyArea,
  useCoverageBetaStore,
} from "@/store/useCoverageBetaStore";
import { values } from "lodash";
import { useMemo } from "react";
import { useShallow } from "zustand/react/shallow";
import { SummaryRow } from "./summary-row/SummaryRow";
import { PersonSummaryRow } from "./summary-row/PersonSummaryRow";
import { SampleOosPrediction } from "@/dev/sample-out-of-stock-predictions";
import { QueryImageControlIcons } from "./query-image-control-icons/QueryImageControlIcons";

interface Props {
  out_of_stock_predictions: SampleOosPrediction[];
}

export const CoverageBetaSummaryView = (props: Props) => {
  const { out_of_stock_predictions } = props;
  const persons = useCoverageBetaStore((s) => s.persons);

  // `values()` returns the same item references the store already holds, so
  // useShallow's array-level comparison is meaningful here — unlike wrapping
  // the object below directly, which would rebuild its arrays (new
  // references) on every call and never compare equal.
  const feEmptyAreas = useCoverageBetaStore(
    useShallow((s) => values(s.idxToFeEmptyArea)),
  );

  const { deletedItems, emptyAreaItems, outOfStockConfirmedItems } =
    useMemo(() => {
      const outOfStockConfirmedItems: FeEmptyArea[] = [];
      const deletedItems: FeEmptyArea[] = [];
      const emptyAreaItems: FeEmptyArea[] = [];

      feEmptyAreas.forEach((feEmptyArea) => {
        const { selectedProducts, isDeleted } = feEmptyArea;

        // Deleted areas retain their products/state (for undo), so classify by
        // isDeleted first — otherwise a deleted area with a product would show
        // under Out of Stock Confirmed instead of Deleted.
        if (isDeleted) {
          return deletedItems.push(feEmptyArea);
        }

        if (selectedProducts.length > 0) {
          return outOfStockConfirmedItems.push(feEmptyArea);
        }

        emptyAreaItems.push(feEmptyArea);
      });

      return { outOfStockConfirmedItems, deletedItems, emptyAreaItems };
    }, [feEmptyAreas]);

  // Keep each person's index in the full `persons` array so the "P{idx}"
  // label matches the box drawn on the query image.
  const indexedPersons = persons.map((person, idx) => ({ person, idx }));
  const activePersons = indexedPersons.filter(
    ({ person }) => !person.isDeleted,
  );
  const deletedPersons = indexedPersons.filter(
    ({ person }) => person.isDeleted,
  );

  return (
    <div className="text-left border border-gray-200 h-full w-full text-xs">
      <QueryImageControlIcons />

      <div className="p-3 space-y-4" data-cb-tour="summary-list">
        <div>
          <div className="font-semibold mb-2">
            Out of Stock Confirmed ({outOfStockConfirmedItems.length})
          </div>

          <div>
            {outOfStockConfirmedItems.map((item) => {
              return (
                <SummaryRow
                  key={item.id}
                  item={item}
                  type="oosConfirmed"
                  out_of_stock_predictions={out_of_stock_predictions}
                />
              );
            })}
          </div>
        </div>

        <div>
          <div className="font-semibold mb-2">
            Empty Area ({emptyAreaItems.length})
          </div>

          <div>
            {emptyAreaItems.map((item) => {
              return (
                <SummaryRow
                  key={item.id}
                  item={item}
                  type="emptyArea"
                  out_of_stock_predictions={out_of_stock_predictions}
                />
              );
            })}
          </div>
        </div>

        <div>
          <div className="font-semibold mb-2">
            Unblurred Persons ({activePersons.length})
          </div>

          <div>
            {activePersons.map(({ person, idx }) => {
              return (
                <PersonSummaryRow key={person.id} person={person} idx={idx} />
              );
            })}
          </div>
        </div>

        <div>
          <div className="font-semibold mb-2">
            Deleted ({deletedItems.length + deletedPersons.length})
          </div>

          <div>
            {deletedItems.map((item) => {
              return (
                <SummaryRow
                  key={item.id}
                  item={item}
                  type="deleted"
                  out_of_stock_predictions={out_of_stock_predictions}
                />
              );
            })}
            {deletedPersons.map(({ person, idx }) => {
              return (
                <PersonSummaryRow key={person.id} person={person} idx={idx} />
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};
